/* eslint-disable react-native/no-inline-styles */
import React, { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { Element, scale, notchTop } from '@components/frame';
import { isPhone, navigateBack } from '@helpers';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { getBoxShadowStyle } from '@styles/shadow';
import Selector from '@styles/selectors';
import Button from '@styles/buttons';
import Line from '@components/common/line';
import BackButton from '@components/common/backButton';

import { clearQueue, updateUploadQueue, startQueue } from '@reducers/uploadQueue';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const QueueInfo = (props) => {
  const { uploadQueue, clearQueue, updateUploadQueue, startQueue, C, currentImage } = props;

  const list = Object.values(uploadQueue).reverse();

  const uploadAgain = (i) => {
    if (i.status !== 'error') {
      return;
    }
    updateUploadQueue({
      ...i,
      status: 'pending',
    });
    startQueue();
  };

  const getImageStatus = (status) => {
    switch (status) {
      case 'pending':
        return <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>Pending</Text>;
      case 'uploading':
        return <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>Uploading</Text>;
      case 'error':
        return <Text style={[font['Subhead 2'], { color: C('Label/Light/Error') }]}>Upload failed</Text>;
      case 'completed':
        return <Text style={[font['Subhead 2'], { color: C('System/Light/Green') }]}>Upload succeeded</Text>;
    }
  };

  return (
    <View style={{ width: '100%', alignItems: 'center', marginTop: scale(20) }}>
      <View style={{ justifyContent: 'flex-end', flexDirection: 'row', width: isPhone ? '100%' : scale(720) }}>
        <Button
          {...{
            variant: 'S',
            type: 'Secondary',
            onPress: () => clearQueue(),
          }}
        >
          {i18n.t('Clear')}
        </Button>
      </View>
      {!list.length && <Text style={[font['Body'], { color: C('Label/Light/Primary'), marginTop: scale(50) }]}>No items</Text>}
      {list.map((item) => (
        <View
          key={item.id}
          style={{
            width: isPhone ? '100%' : scale(720),
            backgroundColor: C('Background/Light/Field'),
            marginTop: scale(20),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            paddingLeft: scale(15),
            paddingRight: scale(15),
            paddingTop: scale(5.5),
            paddingBottom: scale(7),
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {currentImage.fileName === item.name && (
              <View
                style={{
                  width: scale(5),
                  height: scale(5),
                  borderRadius: scale(5),
                  backgroundColor: C('System/Light/Blue'),
                  marginRight: scale(4),
                  marginLeft: scale(-9),
                }}
              />
            )}
            <Text style={[font['Body'], { color: C('Label/Light/Primary') }]}>{item.name}</Text>
          </View>
          <View>{getImageStatus(item.status)}</View>
          <View>
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{item.time}</Text>
          </View>
          {item.status === 'error' && (
            <View style={{ position: 'absolute', right: scale(10), bottom: scale(15) }}>
              <Button
                {...{
                  variant: 'S',
                  type: 'Secondary',
                  onPress: () => uploadAgain(item),
                }}
              >
                {i18n.t('Retry')}
              </Button>
            </View>
          )}
        </View>
      ))}
    </View>
  );
};

const ImageInfo = (props) => {
  const { C, currentImage } = props;

  const { size3x = {} } = currentImage;

  const mainInfo = {
    name: {
      value: currentImage.fileName,
      name: i18n.t('Name'),
      noArrow: true,
    },
    hash: {
      value: currentImage.id,
      name: i18n.t('Hash'),
      noArrow: true,
    },
    pageNumber: {
      value: currentImage.page,
      name: i18n.t('Page Number'),
      noArrow: true,
    },
    pageCount: {
      value: currentImage.pageCount,
      name: i18n.t('Pages'),
      noArrow: true,
    },
    rotatingApp: {
      value: currentImage.customRotate[currentImage.page],
      name: i18n.t('Rotating on App side'),
      noArrow: true,
    },
    widthOriginal: {
      value: currentImage.widthOriginal,
      name: i18n.t('Original width'),
      noArrow: true,
    },
    heightOriginal: {
      value: currentImage.heightOriginal,
      name: i18n.t('Original height'),
      noArrow: true,
    },
    widthOriginal3: {
      value: size3x.width,
      name: i18n.t('Width 3x'),
      noArrow: true,
    },
    heightOriginal3: {
      value: size3x.height,
      name: i18n.t('Height 3x'),
      noArrow: true,
    },
  };

  const cropInfo = {
    rotation: {
      value: (currentImage.cropped ? currentImage.angle : currentImage.customRotate[currentImage.page]) || 0,
      name: i18n.t('Rotation'),
      noArrow: true,
    },
    X: {
      value: currentImage.cropped ? currentImage.x : '-',
      name: i18n.t('Left top X'),
      noArrow: true,
    },
    Y: {
      value: currentImage.cropped ? currentImage.y : '-',
      name: i18n.t('Left top Y'),
      noArrow: true,
    },
    RX: {
      value: currentImage.cropped ? currentImage.x + currentImage.width : '-',
      name: i18n.t('Right bottom X'),
      noArrow: true,
    },
    RY: {
      value: currentImage.cropped ? currentImage.y + currentImage.height : '-',
      name: i18n.t('Right bottom Y'),
      noArrow: true,
    },
  };

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <View style={{ alignItems: 'center', width: '100%' }}>
      <View style={{ width: isPhone ? '100%' : scale(720), height: scale(400), marginTop: scale(20) }}>
        <View style={{ marginLeft: scale(15), marginBottom: scale(4) }}>
          <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>IMAGE DETAILS</Text>
        </View>
        <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
          {Object.keys(mainInfo).map((item, index) => (
            <Line
              {...{
                item: mainInfo[item],
                index,
                array: Object.keys(mainInfo),
                C,
                roundTop: index === 0,
                roundBottom: Object.keys(mainInfo).length - 1 === index,
              }}
              key={item}
            />
          ))}
        </View>
      </View>

      <View style={{ marginTop: scale(isPhone ? 30 : 20), width: isPhone ? '100%' : scale(720), height: scale(400) }}>
        <View style={{ marginLeft: scale(15), marginBottom: scale(4) }}>
          <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>CROP INFORMATION</Text>
        </View>
        <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
          {Object.keys(cropInfo).map((item, index) => (
            <Line
              {...{
                item: cropInfo[item],
                index,
                array: Object.keys(cropInfo),
                C,
                roundTop: index === 0,
                roundBottom: Object.keys(cropInfo).length - 1 === index,
              }}
              key={item}
            />
          ))}
        </View>
      </View>
    </View>
  );
};
const ImageDetails = (props) => {
  const {
    C,
    imageHandling,
    route: {
      params: { id },
    },
  } = props;
  const [activeTab, setActiveTab] = useState('image');

  const { images = [] } = imageHandling;

  const currentImage = images.find((i) => i.id === id);

  if (!currentImage) {
    navigateBack();
    return null;
  }

  const tabs = [
    {
      key: 'image',
      text: 'Image Details',
    },
    {
      key: 'queue',
      text: 'Upload History',
    },
  ];

  return (
    <View style={{ height: '100%' }}>
      <Element
        {...{
          config: {
            position: {
              top: 24,
              left: 0,
            },
            width: 125,
            height: 44,
            justifyContent: 'center',
            zIndex: 100000,
          },
        }}
      >
        <BackButton />
      </Element>
      <View style={{ marginTop: isPhone ? notchTop + scale(17) : scale(50) }}>
        <Text
          style={[
            {
              color: C('Label/Light/Primary'),
              width: '100%',
              textAlign: 'center',
            },
            font['Headline'],
          ]}
        >
          Image Developer menu
        </Text>
      </View>
      <View style={{ width: '100%', justifyContent: 'center', flexDirection: 'row', marginTop: scale(20), paddingBottom: scale(10) }}>
        {tabs.map((item, index) => (
          <Selector key={item.key} {...{ selected: activeTab === item.key, variant: 'L', text: item.text, onPress: () => setActiveTab(item.key) }} />
        ))}
      </View>
      <ScrollView contentContainerStyle={{ backgroundColor: C('Background/Light/Detail') }}>
        <View
          style={{
            width: '100%',
            justifyContent: 'center',
            alignContent: 'center',
            paddingLeft: scale(15),
            paddingRight: scale(15),
          }}
        >
          {activeTab === 'image' && <ImageInfo {...{ ...props, currentImage }} />}
          {activeTab === 'queue' && <QueueInfo {...{ ...props, currentImage }} />}
        </View>
      </ScrollView>
    </View>
  );
};

const WrapperComponent = (props) => {
  const { C } = props;
  return (
    <View style={{ backgroundColor: C('Background/Light/Detail'), width: '100%', height: '100%' }}>
      <ImageDetails {...props} />
    </View>
  );
};

const mapStateToProps = ({ imageHandling, uploadQueue: { uploadQueue } }) => ({ imageHandling, uploadQueue });

export default connect(mapStateToProps, { clearQueue, updateUploadQueue, startQueue })(ColorWrapper(WrapperComponent));
